// ---- the grandson ----
// He is the one the game is really about: Teta makes the food, and he is the
// reason it is made. The camera is on HER and her table (game.ts) — he is the
// one who comes INTO the picture, which is the whole beat, so everything here
// is about keeping him away from the table until there is something on it. He
// wanders a ring well outside it, and the moment a dish lands he looks over,
// runs in to the table's edge, eats, says what it was like (YUM, data.ts) —
// and runs back out. The dish stays where it is: a sofra is what has been
// served, and the table fills up as the game goes on (field.ts).
//
// His model came rigged exactly as Teta's is — the same Hips, LeftToeBase, neck
// — so the loader she and Jiddo share reads him unchanged (core/humanoid.ts),
// and the jointed little figure of primitives that stood in for him until it
// arrived is gone, the way Jiddo's photo cut-out went when his model landed.
//
// Six clips came in the file — five real ones and the single-frame baselayer
// his grandmother has to make an idle out of and he does not need — and between
// them they are everything the brain below already wanted to say: he WALKS
// while he is wandering, RUNS at the food and away to play, stands on one of
// two idles when he is still, and EATS with a clip that is a boy lifting
// something to his mouth with both hands. Which is what the dish itself now
// rides: up off the table, to his mouth, and back down where it was.
import * as THREE from 'three';
import {GLTFLoader} from 'three/addons/loaders/GLTFLoader.js';
import {asset} from '../../core/assets';
import {beep,clipMs,has,pickClip,sayPhrase} from '../../core/audio';
import {bodies,elder,inBounds,intoBounds} from '../../core/field';
import {Animator,readHumanoid} from '../../core/humanoid';
import {scene} from '../../core/scene';
import {clamp,smooth,turnToward} from '../../core/util';
import {pack} from './pack';
import {SOFRA,edge,legClears,outward,rimAt,rimLen,rimS,rimWrap,ring} from './set';
import type {Dish} from './dishes';

export const boy=new THREE.Group(); scene.add(boy);
export const BOY_H=1.35;                       // he is a small boy beside a 2.25 grandmother
// His two idles, and both are used. One is a quiet breath, the other a fidget
// with a hand up by his face; a child who sees the same one every single time
// he stops is watching a loop, and a fresh draw between them each time he comes
// to a stop costs one line.
//
// These are Danny's boy's names for his clips. Another family's boy came out of
// another rigger with names of its own, and his pack says what they are
// (CAST.boyClips, pack.ts); loadBoy reads them before anything plays.
let WALK='Walking', RUN='Running', EAT='Eating';
let IDLES=['Idle_11','Idle_03'];
let anim:Animator|null=null, idle=IDLES[0];
let hands:THREE.Object3D[]=[];
// How far one cycle of each carries him, so his feet are timed by the ground
// rather than by the clock. The numbers are what the clips measure out at, and
// they only stand in if a clip is missing.
let walkStride=1.14, runStride=2.28;
// How far off the ground each clip was authored, and where the loader put his
// feet (clipLifts, core/humanoid.ts).
let lifts:Record<string,number>={}, ground=0;
let eatMs=3533;                                  // the eat clip: one pick-up, one mouthful, one putting-down

export function loadBoy(){
  const {boy:file,boyClips:own}=pack().CAST;
  if(own){WALK=own.walk;RUN=own.run;IDLES=own.idles;idle=IDLES[0];EAT=own.eat??'';}
  new GLTFLoader().loadAsync(asset(file)).then(g=>{
    const m=g.scene, rig=readHumanoid(m,BOY_H,g.animations,file,'boy'); if(!rig)return;
    boy.add(m); ground=m.position.y; const a=new Animator(m,g.animations);
    hands=['LeftHand','RightHand'].map(n=>m.getObjectByName(n)).filter((o):o is THREE.Object3D=>!!o);
    lifts=a.lifts([...rig.feet,...rig.toes]);   // before anything plays: it measures against the rest pose
    walkStride=a.stride(WALK,rig.toes,walkStride);
    runStride=a.stride(RUN,rig.toes,runStride);
    // A boy with no eat clip eats it where it sits, standing on his idle: the
    // eat lasts no time at all, so want() never asks for the clip and the carry
    // puts the dish straight back where it was. The carry rides Danny's eat
    // clip's own timings (PICK, PUT), so a family's own eat clip brings its own.
    eatMs=a.has(EAT)?a.ms(EAT):0;
    anim=a;
  }).catch(err=>console.warn(file+' did not load',err));
}
// ---- his brain: wander | run | eat | off ----
// He runs at food and walks otherwise, which is the whole read: a child who
// sees him break into a run knows the dish is the point. And when he has eaten
// he runs OFF — out to the edge, away from the table — so the next dish never
// lands at his feet and every round has the same shape: she serves, the grass
// is empty for a moment, and then here he comes.
type State='pause'|'walk'|'run'|'eat'|'off';
let state:State='pause', next=900, tx=0, tz=0, target:Dish|null=null;
let head=0, rot=0, phase=0, eatT=-1e9, eatUntil=0, offBy=0, noticeT=0;
let bestG=1e9, bestT=0;                          // the run's nearest approach to its goal so far, and when
const WALKV=1.3, RUNV=3.4, APART=1.05;
// Where he stands to eat: at the table's edge, his own half-width off it, so
// his front is at the wood — measured from the EDGE (edge, set.ts), so it is
// the same half-width along a long side, at an end and round a corner. He used to run to the dish itself, which on a
// cloth on the grass was the natural thing and at a table is a boy walking
// through the furniture. From the rim the dish comes to his hands instead (the
// carry, below), which is what a boy reaching over a table looks like.
const STAND=0.32;
// He wanders a ring about the table rather than the whole field: the camera is
// on it now, so the far corners are off screen, and a boy who spends the round
// out of shot is a boy the game has lost. The near edge of the ring is the
// important one — it is why he is not already standing where the food lands,
// and why there is a moment between her throw and him arriving. Both are how
// far off the table's edge, as every distance from it in this file is.
const KEEP_OUT=2.05, ROAM=5.45;
// And where he bolts to once he has eaten. He has no play clip, so running
// off IS the playing: he clears the table at a run, gets his breath somewhere
// out near the edge, and by the time the next word is spelled he is far enough
// away that coming back for it is a journey worth watching.
const OFF_IN=3.95, OFF_OUT=5.95, OFF_MIN=3.5;      // the last is how far the bolt must be to be worth calling one
const NOTICE=420;                                // the beat between it landing and him seeing it
// When the food is actually AT his mouth: the eat clip's hands take a beat over
// a second to get it there (the carry, below). The chomps used to sound at once,
// which was right for the stand-in's nod and is a boy chewing an empty hand now.
const MOUTH=1250, MUNCH=MOUTH+600;               // the chomps, then the mouthful before he says anything about it
export const boyDebug={get state(){return state},get target(){return target&&target.w},get model(){return anim?.model??null},
  get clip(){return anim?.name??''},get strides(){return [walkStride,runStride]},get lifts(){return lifts},
  get carrying(){return carry&&carry.w}};
// the nearest dish that is on the table and not already eaten
function nearest():Dish|null{
  let best:Dish|null=null, bd=1e9;
  for(const b of bodies as Dish[]){
    if(b.fly||b.eaten)continue;
    const d=Math.hypot(b.position.x-boy.position.x,b.position.z-boy.position.z);
    if(d<bd){bd=d;best=b;}
  }
  return best;
}
// What eating sounds like: three quick chomps, written the way every noise in
// the house is (beep, core/audio.ts) — and then what he says about it, once
// his mouth is empty. Returns what the moment costs the round in quiet.
function relish(d:Dish){
  for(let i=0;i<3;i++)beep(300-30*i,150,.075,'triangle',.12,MOUTH/1000+i*.15);
  const {YUM,YUM_ANY}=pack(), own=YUM(d.w), line=has('R'+own)?own:pickClip(YUM_ANY);
  sayPhrase(line,MUNCH);
  return MUNCH+clipMs('R'+line);
}
// Where he eats it from: the point at the table's edge nearest the dish —
// straight out from it to whichever side it is nearest, STAND off the wood.
// Where two sides are near enough a tie (TIE), the one on HIS side: a dish down
// the middle of the table is as near one long side as the other, and running
// round to the far one for the sake of a few centimetres is a detour a child
// would see. Nudged along the edge, away from his grandmother, if she is
// standing right there — she orbits 0.55 off the edge and he stands 0.32 off
// it, so the two spots can coincide, and APART (below) would shove him off it
// every frame and he would never arrive. The nudges are distances along the
// way round (rimAt, set.ts), so they are the same few strides at an end of the
// table as along a side.
const TIE=0.25;
function rimGoal(d:Dish){
  const p=boy.position, {x:X,z:Z,hx,hz}=SOFRA;
  const dx=clamp(d.position.x-X,-hx,hx), dz=clamp(d.position.z-Z,-hz,hz);
  const feet=[{x:X+dx,z:Z+hz+STAND},{x:X+dx,z:Z-hz-STAND},{x:X+hx+STAND,z:Z+dz},{x:X-hx-STAND,z:Z+dz}];
  const far=(f:{x:number;z:number})=>Math.hypot(f.x-d.position.x,f.z-d.position.z);
  const near=Math.min(...feet.map(far));
  let foot=feet[0], bd=1e9;
  for(const f of feet)if(far(f)<near+TIE){const b=Math.hypot(f.x-p.x,f.z-p.z);if(b<bd){bd=b;foot=f;}}
  const s0=rimS(foot.x,foot.z,STAND);
  for(const k of [0,.35,-.35,.7,-.7,1.1,-1.1,1.5,-1.5,1.9,-1.9]){
    const g=rimAt(s0+k,STAND);
    if(Math.hypot(g.x-elder.position.x,g.z-elder.position.z)>=APART+0.15)return g;
  }
  return rimAt(s0,STAND);
}
// Which way round the table to (gx,gz): the short way, unless his grandmother
// is standing on it. She orbits 0.55 to 1.55 off the edge and he slides at
// 0.32, so she can be right on his way round, and sliding into her met APART
// (below) pushing him back out. So if she is on the short stretch he takes the
// long way, with her behind him. +1 is the way the bearing about the table
// grows, which is the way the distance round it grows (rimS, set.ts).
//
// "On it" is about the room she leaves, not where along it she is: at the far
// edge of her orbit she is over a stride out from his line and he goes by her
// without ever coming within APART, so the long way round would be a detour
// round nobody.
function wayRound(gx:number,gz:number){
  const p=boy.position, sb=rimS(p.x,p.z,STAND), e=elder.position;
  const dg=rimWrap(rimS(gx,gz,STAND)-sb,STAND), de=rimWrap(rimS(e.x,e.z,STAND)-sb,STAND);
  const s=dg>=0?1:-1;
  if(Math.abs(edge(e.x,e.z)-STAND)>=APART)return s;
  return de*s>0&&Math.abs(de)<Math.abs(dg)?-s:s;
}
// How far he still has to go to (gx,gz): along the rim, the way round he
// would take, once he is at it — as the crow flies the far side of the table
// gets no nearer for the first half of the way round — and straight before
// that. What the watchdog below measures progress by.
function remaining(gx:number,gz:number){
  const p=boy.position;
  if(edge(p.x,p.z)>=STAND+0.12)return Math.hypot(gx-p.x,gz-p.z);
  const w=wayRound(gx,gz), L=rimLen(STAND); let da=rimWrap(rimS(gx,gz,STAND)-rimS(p.x,p.z,STAND),STAND);
  if(w>0&&da<0)da+=L; if(w<0&&da>0)da-=L;
  return Math.abs(da);
}
// A step of at most `v` toward (gx,gz) that never goes into the table: at its
// edge, a step that would go in goes ROUND instead (wayRound), along the wood —
// square to the way out from it (outward, set.ts), which along a side is along
// the side and at a corner is round it. That is all the
// pathing there is — the goal is always on the rim (rimGoal) or well outside
// it (the ring, the bolt), so sliding along the wood gets him there, and he
// faces the way he is actually going. Returns how far he moved, for the walk
// cycle.
function stepTo(gx:number,gz:number,v:number){
  const p=boy.position, dx=gx-p.x, dz=gz-p.z, r=Math.hypot(dx,dz); if(r<1e-6)return 0;
  let ux=dx/r, uz=dz/r;
  // Up against his grandmother and still heading into her: round her, the way
  // that leans toward where he is going. Without this the push that keeps him
  // out of her (APART, below) cancelled the whole step and he ran on the spot
  // against her for the watchdog's full second — the wedge between the two of
  // them, which is the thing this file is most about. Before the table's own
  // slide, so a way round her that leads into the wood still goes round the
  // wood rather than into it.
  const ex=elder.position.x-p.x, ez=elder.position.z-p.z, ed=Math.hypot(ex,ez);
  if(ed<APART+0.02&&ed>1e-6&&ux*ex+uz*ez>0){const w=ez*ux-ex*uz>=0?1:-1;ux=w*ez/ed;uz=-w*ex/ed;}
  const n=outward(p.x,p.z);
  // At the edge, or a push off her has just lifted him a hair from it, and
  // heading in: along it instead.
  if(edge(p.x,p.z)<STAND+0.12&&ux*n.x+uz*n.z<0){const w=wayRound(gx,gz);ux=w*n.z;uz=-w*n.x;}
  const m=Math.min(r,v); p.x+=ux*m; p.z+=uz*m; head=Math.atan2(ux,uz);
  return m;
}
// Not in the table, whatever else moved him — a push off his grandmother, a
// bolt drawn across it. Out to his line by the nearest way off the wood.
function keepOff(){
  const p=boy.position, e=edge(p.x,p.z);
  if(e<STAND){const n=outward(p.x,p.z);p.x+=n.x*(STAND-e);p.z+=n.z*(STAND-e);}
}
// Off to play. Anywhere out on the far ring that is a real run from here — a
// two-step shuffle would not read as leaving — that does not run straight
// across the table from the rim he is standing at, and a timeout in case the
// field is too boxed in to get there.
function runOff(t:number){
  // Two passes. The first wants a leg that misses the table outright; the
  // second takes any leg at all, because a bolt that grazes the rim is handled
  // by the slide (stepTo) and a bolt that never happens is not. He eats at the
  // rim now, and from the near side of it the whole far ring is on the other
  // side of the table: one pass rejected every draw about a quarter of the
  // time, and he ambled off at walking pace instead of bolting.
  for(let pass=0;pass<2;pass++)for(let i=0;i<14;i++){
    const {x,z}=ring(Math.random()*Math.PI*2,OFF_IN+Math.random()*(OFF_OUT-OFF_IN));
    if(!inBounds(x,z,0.5))continue;
    if(Math.hypot(x-boy.position.x,z-boy.position.z)<OFF_MIN)continue;
    if(!pass&&legClears(boy.position.x,boy.position.z,x,z)<STAND-0.1)continue;
    tx=x; tz=z; state='off'; offBy=t+4500; return;
  }
  state='pause'; next=t+700;
}
// ---- the dish in his hands ----
// The eat clip is a boy lifting something to his mouth with both hands: they
// come together in front of him over the first second, rise to his face, hold
// there while he chews, and come back down at the end of it. So the dish does
// what the hands do. He eats at the RIM and the dish is out on the top, so a
// plate that stayed where it was while he mimed over it would be the one thing
// about the moment a three-year-old would have asked about — and the reach
// across the wood that the stand-in's arms used to make is this instead.
//
// It is not parented to a hand. A dish is a top-level object and the flight
// already drove it that way (dishes.ts), so each frame it is simply PUT where
// his two hand bones are, and the pick-up and the putting-down are a weight
// easing between its own spot on the table and that point. Nothing here has to
// know what shape a hand is, and it goes back to the spot it came from — which
// it keeps hold of as `home`, so that nothing is served into the gap it has
// left on the top, and so it is still part of the spread (field.ts).
//
// Seconds into the clip, read off it: his hands close on it, they have it up,
// they start back down, it is down.
const PICK=[0.25,0.95], PUT=[3.05,3.45];
// Where between his hands it sits: out along his facing so it is in front of
// him rather than through his chest, and a little under his palms, since a dish
// group's origin is the bottom of the dish. RISE arcs it on the way in and out,
// so it is lifted and set down rather than slid. And smaller than it sits on
// the table: at full size a bowl is two and a half times the width of his head
// and covers the face the clip is the whole point of.
const CARRY=0.62, FWD=0.08, DROP=0.04, RISE=0.12;
let carry:Dish|null=null;
const HAND=new THREE.Vector3(), H2=new THREE.Vector3();
function carryW(s:number){
  if(s<PICK[0])return 0;
  if(s<PICK[1])return smooth((s-PICK[0])/(PICK[1]-PICK[0]));
  if(s<PUT[0])return 1;
  if(s<PUT[1])return 1-smooth((s-PUT[0])/(PUT[1]-PUT[0]));
  return 0;
}
// Timed by how far into the eat he is, which is the clip's own time: it plays
// unpaused from eatT, so the two agree. And it ends where the CLIP ends rather
// than where the brain does — his hands are back down by then and the dish is
// back on its spot, so it is never let go of in mid-air however long the line
// he is saying over the top of it runs. Without a model there are no hands to
// ride and it simply stays where it is.
function carryDish(t:number){
  const d=carry!, s=(t-eatT)/1000, from=d.home;
  if(s>=eatMs/1000||!anim||hands.length<2){d.position.copy(from);d.scale.setScalar(d.full);d.held=false;carry=null;return;}
  const w=carryW(s);
  hands[0].getWorldPosition(HAND); hands[1].getWorldPosition(H2);
  HAND.add(H2).multiplyScalar(0.5);
  HAND.x+=Math.sin(rot)*FWD; HAND.z+=Math.cos(rot)*FWD; HAND.y-=DROP;
  d.position.set(from.x+(HAND.x-from.x)*w,
    from.y+(HAND.y-from.y)*w+RISE*Math.sin(Math.PI*w),
    from.z+(HAND.z-from.z)*w);
  d.scale.setScalar(d.full*(1-w*(1-CARRY)));
}
// ---- what he is playing ----
function want(t:number,playing:string){
  if(state==='eat'&&t-eatT<eatMs)return EAT;     // and after that he stands, if his line is still running
  if(state==='walk')return WALK;
  if(state==='run'||state==='off')return RUN;
  if(IDLES.indexOf(playing)<0)idle=IDLES[Math.random()*IDLES.length|0];   // a fresh draw each time he comes to a stop
  return idle;
}
export function updateBoy(t:number,dt:number,onEaten:(d:Dish)=>void){
  const p=boy.position;
  if(state==='eat'){
    // eatUntil already covers his own line (relish returns its length). It used
    // to wait on speaking() too, which is the field's HUSH — and the hush runs
    // for the whole gap between rounds and the whole of the next word's name,
    // so he stayed crouched over an empty patch of grass for up to nine and a
    // half seconds. The hush is for not making NOISE: jiddo's animals walk
    // about all through it and only hold their cries (brains.ts). Running is
    // silent.
    if(t>eatUntil)runOff(t);                     // eaten: he does not hang about waiting for the next one
  }else if(state==='off'){
    // Nothing calls him back mid-bolt, not even a dish landing behind him.
    // That is the whole point of it: the gap is guaranteed rather than hoped for.
    const r=Math.hypot(tx-p.x,tz-p.z);
    if(r<0.3||t>offBy){state='pause';next=t+900+Math.random()*1600;}
    else phase+=stepTo(tx,tz,RUNV*dt)/runStride;
  }else{
    const d=nearest();
    // Something new on the table. He looks over first and sets off a beat
    // later: a child watching needs that beat to read him as having SEEN it,
    // and it is the difference between a boy who runs to the food and a boy who
    // was apparently expecting it.
    if(d!==target){target=d;noticeT=d?t+NOTICE:0;if(d)head=Math.atan2(d.position.x-p.x,d.position.z-p.z);}
    if(target&&(target.eaten||target.fly))target=null;
    if(target&&t<noticeT){state='pause';}
    else if(target){
      const g=rimGoal(target), r=Math.hypot(g.x-p.x,g.z-p.z), rem=remaining(g.x,g.z);
      // getting there — or a new plan (she moved, he reached the rim and the
      // way round is longer than the line was), which is not the same as stuck
      if(state!=='run'||rem<bestG-0.04||rem>bestG+0.5){bestG=rem;bestT=t;}
      // The watchdog. The two slides above are what keep him moving — round
      // the table, round her — and it is what stands behind them, because a
      // boy stuck against a table for the rest of the game is the one failure
      // a child cannot tap past. It is not theoretical: before the slide round
      // her, a run straight at a standing grandmother reached it in about one
      // approach in 250. A
      // second with no progress: if the dish is within his reach across the
      // wood he eats from where he stands, and if it is not, one step to the
      // side breaks whatever balance held him, and he tries again.
      const stuck=t-bestT>1000, cr=edge(p.x,p.z);
      const toDish=Math.hypot(target.position.x-p.x,target.position.z-p.z);
      if(r<0.14||(stuck&&toDish<1.7&&cr<STAND+0.25)){
        // He has got there: he faces it across the wood, picks it up, eats, and
        // says so. It goes back on the table; the field is one dish more eaten,
        // not one lighter. `held` is what keeps the field's shuffle and its
        // clamp off it while it is in the air (field.ts).
        state='eat'; head=Math.atan2(target.position.x-p.x,target.position.z-p.z);
        target.eaten=true; eatT=t; carry=target; target.home.copy(target.position); target.held=true;
        eatUntil=t+Math.max(eatMs,relish(target));
        onEaten(target); target=null;
      }else{
        // ...and the step goes to whichever side she is NOT on. A fixed hand
        // could put her from one side of his line to the other and line the
        // two of them up again, for a second stall.
        if(stuck){const w=(elder.position.x-p.x)*Math.cos(head)-(elder.position.z-p.z)*Math.sin(head)>0?-1:1;
          p.x+=w*0.35*Math.cos(head);p.z-=w*0.35*Math.sin(head);bestG=1e9;bestT=t;}
        state='run'; phase+=stepTo(g.x,g.z,RUNV*dt)/runStride;
      }
    }else if(state==='walk'){
      const r=Math.hypot(tx-p.x,tz-p.z);
      if(r<0.2){state='pause';next=t+1400+Math.random()*2200;}
      else phase+=stepTo(tx,tz,WALKV*dt)/walkStride;
    }else if(t>next){
      // Somewhere on the ring, on the grass. The draws are so that a point
      // pulled back inside the field does not land back at the table; the
      // last one stands whether or not it did, because him wandering a little
      // close is not worth a frame's worth of arithmetic.
      // ...and a LEG that stays out there too. Testing only the destination
      // was the bug that ate the whole beat: both ends of a wander can sit on
      // the ring while the straight line between them runs right across the
      // table, and about one leg in five did. Then a word finishes while he is
      // standing at the table, the dish lands beside him, and he takes a step
      // and eats it — no run, no arrival, no gap, which is the one thing this
      // is all for.
      for(let i=0;i<10;i++){
        const s=intoBounds(ring(Math.random()*Math.PI*2,KEEP_OUT+Math.random()*(ROAM-KEEP_OUT)),0.5); tx=s.x; tz=s.z;
        if(edge(tx,tz)<KEEP_OUT-0.4)continue;
        // The bar is the ring, or how far out he already is if a bolt was cut
        // short and left him inside it — otherwise nothing would ever pass and
        // he would be stuck walking the one leg the draws could not better.
        const bar=Math.min(KEEP_OUT-0.4,edge(p.x,p.z)-0.05);
        if(legClears(p.x,p.z,tx,tz)>bar)break;
      }
      state='walk';
    }
  }
  // Keep him out of his grandmother rather than through her. A stride and a bit,
  // not the 0.85 it was: she stands right off the table's edge and he comes to
  // its rim, so at 0.85 the two of them merged into one silhouette from the
  // camera, which is the opposite of the point. rimGoal keeps his spot at the
  // table APART+0.15 from her, so this push never takes him off it; and when
  // she strolls past him while he eats, it slides him along the rim rather
  // than through her, and keepOff keeps the slide on the rim.
  const gx=p.x-elder.position.x, gz=p.z-elder.position.z, gd=Math.hypot(gx,gz);
  if(gd<APART&&gd>0.001){p.x=elder.position.x+gx/gd*APART;p.z=elder.position.z+gz/gd*APART;}
  keepOff();
  rot=turnToward(rot,head,6*dt); boy.rotation.y=rot;
  if(anim){
    // The eat runs ONCE and holds its last frame. It is a little scene with a
    // beginning and an end — he picks the dish up and he puts it down — and
    // looping it would have him eat the same plate twice.
    const w=want(t,anim.name); if(w!==anim.name)anim.play(w,0.25,w===EAT);
    anim.update(dt,anim.name===WALK||anim.name===RUN?phase:undefined);
    // Each clip stands at its own height, and Idle_11 stands seven centimetres
    // up (clipLifts, core/humanoid.ts). What is showing mid-crossfade is a
    // weighted blend of two poses, so the height it stands at is that same
    // weighting of the two clips' own (lift, core/humanoid.ts) rather than a
    // chase after the incoming one. Eased at a guessed rate instead, the drop
    // arrived a third of a second after the pose did and he hovered for a
    // moment every single time he came to a stop on Idle_11, which is the one
    // thing the measurement is for.
    anim.model.position.y=ground-anim.lift(lifts);
  }
  if(carry)carryDish(t);                         // after the pose, so the dish is where his hands are NOW
}
